import * as ReactInstance from 'react'

import { featureFlags } from '@inno-tools/container-bridge'
import * as QueryInstance from '@inno-tools/query'
import queryPackageJson from '@inno-tools/query/package.json'
import {
  ModuleFederation,
  type ModuleFederationRuntimePlugin,
  createInstance,
  registerGlobalPlugins
} from '@module-federation/enhanced/runtime'
import { RetryPlugin } from '@module-federation/retry-plugin'
import * as ReactDOMInstance from 'react-dom'
import reactDomPackageJson from 'react-dom/package.json'
import reactPackageJson from 'react/package.json'
import { logger } from 'shared/services/logger'

import packageJson from '../package.json'
import { innoUiLoaders } from './federation.innoUiLoaders'
import {
  ANCHOR_OFF_FLAG,
  innoUiShares,
  isInnoUiAnchorEnabled
} from './federation.innoUiShares'
import {
  remoteFailed,
  remoteRequested,
  shareReusePlugin
} from './federation.shareReuse'

const EXCLUDED_DEPENDENCIES = [
  '@inno-ui/icons',
  '@inno-ui/file-icons',
  '@inno-ui/app-icons',
  '@innoscripta/icons'
]

// Shares register under the version actually bundled, never the package.json
// range: `satisfy('^18.3.1', '^18.3.1')` is false, so a range-keyed copy never
// fits any consumer and the share-reuse picker hands remotes their own React.
// Read from package.json, not `ReactDOM.version`: that reports a prerelease
// ("18.3.1-next-…") which fits neither "^18.3.1" nor "18.3.1", so the picker
// skipped it and only the runtime's singleton fallback still handed it out.
const sharedVersions = {
  react: reactPackageJson.version,
  'react-dom': reactDomPackageJson.version,
  '@inno-tools/query': queryPackageJson.version
}

const deps = Object.fromEntries(
  Object.entries(packageJson.dependencies).filter(([key]) => {
    return !EXCLUDED_DEPENDENCIES.includes(key)
  })
)

let instance: ModuleFederation | null = null

// scope → the remoteEntry URL currently registered for it. Module Federation
// silently ignores registerRemotes for a name it already knows unless `force`
// is set, so without this the CDN → direct-URL fallback would re-load the same
// failed entry instead of the fallback one.
const registeredEntries = new Map<string, string>()

/** The registered scope a `<scope>/<expose>` id belongs to; scopes may contain `/`. */
function scopeOf(id: string) {
  let match: string | undefined
  for (const scope of registeredEntries.keys())
    if (
      (id === scope || id.startsWith(`${scope}/`)) &&
      scope.length > (match?.length ?? -1)
    )
      match = scope
  return match ?? id.split('/')[0]
}

const fallbackAppPlugin: () => ModuleFederationRuntimePlugin = function () {
  return {
    name: 'fallback-app-plugin',
    errorLoadRemote(args) {
      // ids are `<scope>/<expose>`; shared modules waiting on this remote's
      // versions must not sit out the full boot-wait cap.
      remoteFailed(scopeOf(String(args.id)))
      logger.exception(
        'federation.remote_load_failed',
        args.error,
        { lifecycle: args.lifecycle, remote: args.id },
        { category: 'federation' }
      )
      throw new Error(`Failed to load remote module: ${args.error}`)
    }
  }
}

/**
 * The container's own copy of every @inno-ui package, registered lazily so
 * in-range remotes share it in any load order (docs/inno-ui-shared-anchor.md).
 * Read once, when the instance is created. Remotes register only after
 * `whenFeatureFlagsReady`, so backend flags are published by then unless the
 * first fetch took longer than its 3 s timeout (see the spec's off-switch limits).
 */
function innoUiAnchorShares() {
  let storage: Storage | undefined
  try {
    storage = window.localStorage
  } catch {
    // Sandboxed frames throw on access; the other switches decide.
  }
  const enabled = isInnoUiAnchorEnabled({
    buildFlag: process.env.REACT_APP_MF_INNO_UI_ANCHOR,
    storage,
    backendOff: featureFlags.active('global', ANCHOR_OFF_FLAG)
  })
  return enabled ? innoUiShares(innoUiLoaders, __INNO_UI_VERSIONS__) : {}
}

function notifyServiceWorker(entryUrl: string, scope: string) {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'REGISTER_FEDERATION_ORIGIN',
      url: entryUrl,
      appId: scope
    })
  }
}

export function registerRemote(
  entryUrl: string,
  scope: string
): ReturnType<typeof createInstance> {
  // Defensive: if a federationUrl is missing in env, the template literal
  // below would produce `undefined/remoteEntry.js` and the SPA dev server
  // would respond with index.html → SyntaxError("<"). Bail loudly instead.
  if (
    !entryUrl ||
    typeof entryUrl !== 'string' ||
    !entryUrl.trim() ||
    entryUrl === 'undefined'
  ) {
    throw new Error(
      `registerRemote called with invalid entryUrl ("${entryUrl}") for scope "${scope}". ` +
        'Check the corresponding REACT_APP_*_DEV_PATH or REACT_APP_*_CDN_URL remote URL env var.'
    )
  }
  notifyServiceWorker(entryUrl, scope)
  remoteRequested(scope)
  const entry = `${entryUrl}/remoteEntry.js`
  if (!instance) {
    // Global, not in `plugins` below: remotes resolve shares in their own
    // instances and only pick up global plugins. Registered here, before the
    // first remoteEntry runs, so every remote instance gets it.
    registerGlobalPlugins([
      shareReusePlugin({
        // A deployed remote bundles a copy below another remote's range; the
        // fix is refreshing that app's lockfile, so say which one.
        onSecondCopy: copy => logger.warn('federation.shared_second_copy', copy)
      })
    ])
    instance = createInstance({
      name: 'container',
      remotes: [{ name: scope, entry }],

      shared: {
        react: {
          version: sharedVersions['react'],
          lib: () => ReactInstance,
          shareConfig: {
            singleton: true,
            requiredVersion: deps['react']
          }
        },
        // MF exports react-dom/index.js by default. Need to share react-dom/client explicitly
        // Check REACT_APP_DOMAIN/local/projects/path/app-name/webpack/runtime/sharing in the Source tab in the browser to make sure
        'react-dom/client': {
          version: sharedVersions['react-dom'],
          lib: () => ReactDOMInstance,
          shareConfig: {
            singleton: true,
            requiredVersion: deps['react-dom']
          }
        },
        'react-dom': {
          version: sharedVersions['react-dom'],
          lib: () => ReactDOMInstance,
          shareConfig: {
            singleton: true,
            requiredVersion: deps['react-dom']
          }
        },
        '@inno-tools/query': {
          version: sharedVersions['@inno-tools/query'],
          lib: () => QueryInstance,
          shareConfig: {
            singleton: true,
            requiredVersion: deps['@inno-tools/query']
          }
        },
        ...innoUiAnchorShares()
      },
      shareStrategy: 'loaded-first',
      plugins: [
        RetryPlugin({
          fetch: {},
          script: {}
        }),
        fallbackAppPlugin()
      ]
    })
    // The runtime puts a share in the scope at creation only when it has a
    // `lib`; the lazy @inno-ui shares (`get` only) land there when the host
    // initialises sharing, which loading a remote never does. Register them now,
    // before the first remote boots. Under 'loaded-first' this loads no remote.
    instance.initializeSharing('default')
  } else if (registeredEntries.get(scope) !== entry) {
    // `force` only matters when the scope is already registered under a
    // different entry (CDN failed → falling back to the direct URL). Skipping
    // the call entirely when the entry is unchanged avoids tearing down an
    // already-loaded remote on every mount.
    instance.registerRemotes([{ name: scope, entry }], { force: true })
  }

  registeredEntries.set(scope, entry)

  return instance
}
